import { IImage } from "@faire/web-api/indigofair/data/IImage";
import { ILeanProductTile } from "@faire/web-api/indigofair/data/ILeanProductTile";
import { optimizeImageUrl } from "@faire/web/common/images/optimizeImages";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { optimizeSwatchImage } from "@faire/web/slate/Swatch/Swatch";
import { Typography } from "@faire/web/slate/Typography";
import React from "react";
import styled from "styled-components";

import { ProductTileInfo } from "@faire/retailer/features/MarketplaceItems/ProductTileInfo";
import { getSettingProductTileOptionDetails } from "@faire/retailer/settings/getSettingProductTileOptionDetails";
import { getSettingProductTileOptionDetailsSwatchesImageOptimization } from "@faire/retailer/settings/getSettingProductTileOptionDetailsSwatchesImageOptimization";

const getOptimizedSwatchImageSrc = (image: IImage, isSSR: boolean) => {
  if (getSettingProductTileOptionDetailsSwatchesImageOptimization()) {
    return optimizeImageUrl(image, isSSR, {
      width: 360,
      height: 360,
      resizeMethod: "fill",
      dprOverride: 1,
    });
  } else {
    return optimizeSwatchImage(image, isSSR);
  }
};

export const ProductTileSwatches: React.FC<{
  optionDetails?: ILeanProductTile.IOptionsDetails;
}> = ({ optionDetails }) => {
  const isSSR = useIsSSR();
  if (!getSettingProductTileOptionDetails() || !optionDetails) {
    return null;
  }

  if (optionDetails?.text) {
    return <StyledTypography>{optionDetails.text}</StyledTypography>;
  }

  return (
    <StyledSwatchesRow>
      <StyledSwatchesImageRow align="center">
        {optionDetails.swatch_details?.swatch_preview_images.map((image) => (
          <Image
            src={getOptimizedSwatchImageSrc(image, isSSR)}
            key={image.token}
          />
        ))}
      </StyledSwatchesImageRow>
      <Spacer size="1/2x" />
      <Typography variant="paragraphSansRegular">
        {optionDetails.swatch_details?.formatted_remaining_option_count}
      </Typography>
    </StyledSwatchesRow>
  );
};

/**
 * Eventually this should be a single component consuming ILeanProductTiles,
 * however on brand page we use ProductTileInfo instead.
 */
export const BrandPageProductTileSwatches: React.FC<{
  productTileInfo: ProductTileInfo;
}> = ({ productTileInfo }) => {
  return (
    <ProductTileSwatches optionDetails={productTileInfo.options_details} />
  );
};

const StyledSwatchesImageRow = styled(Row)`
  margin-left: -1px;
  > img:nth-child(n + 2) {
    margin-left: -4px;
  }
`;

const Image = styled.img`
  width: ${getSpacing("2x")};
  border: 1px solid white;
  height: ${getSpacing("2x")};
  border-radius: 100%;
  object-position: center;
`;

const StyledSwatchesRow = styled(Row).attrs({
  align: "center",
})`
  height: 20px;
`;

const StyledTypography = styled(Typography).attrs({
  variant: "paragraphSansRegular",
})`
  line-height: 20px;
`;
